import { LoggerLevel, LoggerSource } from './LoggerLevel';

// SERVICE
import HttpService from '@Services/HttpService/HttpService';

// TYPES
import { IWaitForEventTimestampOptions, LoggerTarget, TLoggerParameters, TLoggerState } from './Logger.types';

// TOOLS
import { getConsoleColors, getConsoleLabel } from './Logger.tools';

// DEFAULTS
import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { TUrlParameters } from '@ConfigLoader/Configs/ConnectionConfig';
import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ETurboModeLevels, SpinSpeed } from '@GFTypes/GameStoreTypes';
import { IKibanaData } from '@GFTypes/Kibana';
import { IActionResponseData } from '@GFTypes/Responses/ActionResponse';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter, { IEventData } from '@Managers/Events/EventsCenter';
import { EGameEvents, TUserAction } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { getCurrentTurboLevel } from '@Managers/TurboManager/TurboManager';
import {
    GameRenderer,
    IKibanaErrorPayload,
    IKibanaGameStartPayload,
    IKibanaSpinSummaryPayload,
    IKibanaUserInformation,
    IStatistics,
    IUserAgentInfo,
    KibanaLogPayload
} from '@Services/HttpService/HttpService.types';
import { WebGLRenderer } from 'pixi.js';
import { DefaultState } from './Logger.defaults';
import { UserAgentParser } from './UserAgentParser';

/**
 * Logger class used to log messages to console in different debug levels.
 */
class Logger {
    private active: boolean = false;
    private filterNames: Array<string> = [];
    private kibanaConfig: IKibanaData;
    private readonly loggerState: TLoggerState;
    private spinBet: number | undefined;
    private readonly userAgentParser = new UserAgentParser();

    private kibanaUserInformation: IKibanaUserInformation = {};

    /**
     * Logger constructor.
     */
    constructor() {
        this.loggerState = DefaultState;
    }

    /**
     * Get Logger Levels general state.
     */
    public getState() {
        return this.loggerState;
    }

    /**
     * Updates the logger configuration for a given level or source.
     *
     * @param data Configuration object with the following properties:
     *  @param data.category - Specifies whether to update a log "level" or "source".
     *  @param data.key - The specific {@link LoggerLevel} or {@link LoggerSource} to update.
     *  @param data.target - The target output for the update ({@link LoggerTarget.CONSOLE} or {@link LoggerTarget.KIBANA}).
     *  @param data.value - The boolean value to set (true to enable, false to disable).
     */
    public setState(data: { category: keyof TLoggerState; key: LoggerLevel | LoggerSource; target: LoggerTarget; value: boolean }): TLoggerState {
        const { category, key, target, value } = data;

        if (category === 'levels') {
            const levelKey = key as LoggerLevel;
            this.loggerState.levels[levelKey][target] = value;
        } else if (category === 'sources') {
            const sourceKey = key as LoggerSource;
            this.loggerState.sources[sourceKey][target] = value;
        }

        return this.loggerState;
    }

    /**
     * Sets filter.
     * @param name Filter name
     */
    public setFilterName(name: string) {
        if (!this.filterNames.includes(name)) this.filterNames.push(name);
        return this.filterNames;
    }

    /**
     * Deletes filter.
     * @param name Filter name
     */
    public deleteFilterName(name: string) {
        this.filterNames = this.filterNames.filter((item) => item !== name);
        return this.filterNames;
    }

    /**
     * Gets array with filters.
     * @return filterNames array
     */
    public getFilters() {
        return this.filterNames;
    }

    /**
     * Clears filters.
     */
    public clearFilters() {
        this.filterNames = [];
        return this.filterNames;
    }

    public activeState(mode: boolean) {
        this.active = mode;
    }

    /**
     * Initializes Logger service.
     */
    public init(urlParameters: TUrlParameters, kibanaConfig?: IKibanaData) {
        if (!kibanaConfig) return;

        this.kibanaConfig = kibanaConfig;
        const { languageCode, ...userInfo } = urlParameters;
        const gameList = ConfigLoader.getConfiguration(EConfigurationNames.GAME_LIST);
        this.kibanaUserInformation = userInfo as IKibanaUserInformation;
        if (userInfo.gameId) this.kibanaUserInformation.gameName = gameList[userInfo.gameId] ?? 'Unknown Game';
        for (const level of this.kibanaConfig.levels) {
            this.loggerState.levels[level].kibana = true;
        }
        for (const source of this.kibanaConfig.sources) {
            this.loggerState.sources[source].kibana = true;
        }
        HttpService.set({ kibanaUrl: kibanaConfig.url });
        EventsCenter.on(EGameStates.SPIN_STATE, this.gatherSpinInfo, this);
        EventsCenter.on(EGameEvents.SEND_ACTION_REQUEST, this.setBet, this);
        EventsCenter.once(EGameStates.GAME_START, this.sendGameStartKibanaPayload, this);
        this.setupGlobalErrorHandlers();
    }

    private setBet(data: TUserAction) {
        if (data.actionName !== EActionName.SPIN && data.actionName !== EActionName.BONUS_BUY_SPIN) return;
        this.spinBet = data.bet;
    }

    private logPrint(level: LoggerLevel, parameters?: TLoggerParameters) {
        const { c0, c1, c2, c3 } = getConsoleColors(level);

        if (parameters?.source !== undefined) {
            parameters.sourceName = LoggerSource[parameters.source];
        }
        console.groupCollapsed(getConsoleLabel(level, parameters), c0, c1, c2, c3);
        console.info(parameters);
        console.trace();
        console.groupEnd();
    }

    private parseUserAgent(ua: string): IUserAgentInfo {
        return this.userAgentParser.parse(ua);
    }

    /**
     * Logs messages to Console.
     * @param level LoggerLevel to log
     * @parameters - additional information parameters:
     *      @param origin of console log
     *      @param message message to display in console
     *      @param data optional parameter for data display: eg. object, parsed jsons
     *      @param separator for separator printing
     *      @param label - optional for filtering purpose
     */
    public log(level: LoggerLevel, parameters: TLoggerParameters, kibanaPayload?: KibanaLogPayload) {
        try {
            if (!parameters) return;

            const { source } = parameters;
            const sourceState = this.loggerState.sources[source];
            const levelState = this.loggerState.levels[level];

            if (!sourceState.console && !sourceState.kibana) return;

            if (sourceState.kibana && levelState.kibana) {
                HttpService.kibanaLog({ level, log: parameters, ...kibanaPayload });
            }

            if (!this.active || !sourceState.console || !levelState.console) return;

            if (this.filterNames.length > 0) {
                if (parameters.label && this.filterNames.includes(parameters.label)) {
                    this.logPrint(level, parameters);
                }
                return;
            }

            this.logPrint(level, parameters);
        } catch {}
    }

    public warn(message: string) {
        if (this.active) {
            console.warn(message);
        }
    }

    public getScreenInfo() {
        const { innerWidth: width, innerHeight: height, devicePixelRatio: dpr } = window;
        const orientation = gameInstance?.gameStore.get(EGameStoreKeys.CurrentOrientation);

        return { width, height, dpr, orientation };
    }

    public createSpinSummaryMessage(logParameters: TLoggerParameters, statistics: IStatistics, actionName: EActionName): IKibanaSpinSummaryPayload | undefined {
        try {
            const userData = this.kibanaUserInformation;

            const gameSettingsLocalStorage = userData.gameId ? localStorage.getItem(userData.gameId.toString()) : null;
            const gameSettings = gameSettingsLocalStorage ? JSON.parse(gameSettingsLocalStorage) : {};
            const wcaMode = !!gameSettings.wcaMode && gameSettings.wcaMode.toLowerCase() === 'true';
            const leftHandMode = !!gameSettings.leftHandMode && gameSettings.leftHandMode.toLowerCase() === 'true';
            const soundMuted = gameInstance.soundManager.isMuted();
            const fullscreen = gameInstance.gameStore.get(EGameStoreKeys.IsFullscreen);
            const level = getCurrentTurboLevel(gameInstance.gameStore);
            const spinSpeed = ETurboModeLevels[level] as SpinSpeed;
            const { isEnabled: autoMode } = gameInstance.gameStore.get(EGameStoreKeys.AutoPlayState);
            const screen = this.getScreenInfo();
            const { app, env } = this.kibanaConfig;
            const spinSummaryPayload: IKibanaSpinSummaryPayload = {
                actionName: actionName,
                userData,
                env,
                app: app ?? 'games',

                log: logParameters,

                screen,

                statistics,

                settings: {
                    wcaMode,
                    leftHandMode,
                    soundMuted,
                    fullscreen,
                    spinSpeed,
                    autoMode
                }
            };
            return spinSummaryPayload;
        } catch {
            return undefined;
        }
    }

    private createGameStartMessage(logParameters: TLoggerParameters): IKibanaGameStartPayload | undefined {
        try {
            const userData = this.kibanaUserInformation;
            const { app, env } = this.kibanaConfig;
            const renderer = gameInstance.renderer;
            let gameRenderer = GameRenderer.CANVAS;
            let gpuVendor: string | null = null;
            let gpuRenderer: string | null = null;

            if (renderer instanceof WebGLRenderer) {
                gameRenderer = GameRenderer.WEBGL;
                const gl = renderer.gl;
                if (gl && typeof gl.getExtension === 'function') {
                    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                    if (debugInfo) {
                        gpuVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string;
                        gpuRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
                    }
                }
            }

            const ua = navigator.userAgent;
            const userAgentInfo = this.parseUserAgent(ua);

            const telemetry = {
                gameRenderer,
                cpuCores: navigator.hardwareConcurrency ?? null,
                memoryGB: (navigator as any).deviceMemory ?? null,
                gpuVendor,
                gpuRenderer
            };

            const screen = this.getScreenInfo();

            const payload: IKibanaGameStartPayload = {
                userData,
                env,
                app: app ?? 'games',
                log: logParameters,
                screen,
                deviceInfo: {
                    userAgent: ua,
                    userAgentInfo: userAgentInfo,
                    telemetry
                }
            };

            return payload;
        } catch {
            return undefined;
        }
    }

    private createErrorMessage(error: Error | string, source: LoggerSource = LoggerSource.Game): IKibanaErrorPayload {
        error ??= 'undefined';
        const message = typeof error === 'string' ? error : error.message;
        const stack = typeof error === 'string' ? undefined : error.stack;

        const screen = this.getScreenInfo();
        const { app, env } = this.kibanaConfig ?? {};

        return {
            userData: this.kibanaUserInformation,
            env: env ?? 'unknown',
            app: app ?? 'games',
            log: {
                source,
                message,
                sourceName: LoggerSource[source]
            },
            screen,
            errorInformation: stack || message
        };
    }

    private waitForEventTimestamp<T>({ event, eventIds = [], validator }: IWaitForEventTimestampOptions<T>): Promise<{ timestamp: number; data: T }> {
        return new Promise((resolve) => {
            const handler = (data: T) => {
                if (eventIds.length > 0) {
                    const { eventIds: receivedIds = [] } = data as IEventData;
                    for (let i = 0; i < eventIds.length; i++) {
                        if (receivedIds[i] !== eventIds[i]) return;
                    }
                }
                if (validator && !validator(data)) return;
                EventsCenter.off(event, handler);
                resolve({
                    timestamp: performance.now(),
                    data
                });
            };
            EventsCenter.on(event, handler);
        });
    }

    private getElapsedTime(timestampStart: number, timestampEnd: number): number {
        return timestampEnd - timestampStart;
    }

    private async gatherSpinInfo() {
        if (gameInstance.gameStore.get(EGameStoreKeys.GameHistoryState).isHistoryMode) return;

        gameInstance.fpsTracker.startSession();

        const spinStart = performance.now();
        const lastReelEventIndex = gameInstance.gameStore.get(EGameStoreKeys.NumberOfReels);

        const spinResponsePromise = this.waitForEventTimestamp<{
            response: IActionResponseData & { actionName: EActionName };
        }>({
            event: EGameEvents.ACTION_RESPONSE_RECEIVED,
            validator: ({ response }) => response?.actionName === EActionName.SPIN || response?.actionName === EActionName.BONUS_BUY_SPIN
        });

        const takeResponsePromise = Promise.race([
            this.waitForEventTimestamp<{ response: IActionResponseData & { actionName: EActionName } }>({
                event: EGameEvents.ACTION_RESPONSE_RECEIVED,
                validator: ({ response }) => response?.actionName === EActionName.TAKE
            }),
            spinResponsePromise
        ]).then((response) => {
            return response.data.response.actionName === EActionName.TAKE ? response : undefined;
        });

        const spinEndPromise = this.waitForEventTimestamp<IEventData>({
            event: EGameEvents.REEL_SPIN_END_$,
            eventIds: [lastReelEventIndex]
        });

        const normalSpinEndPromise = Promise.race([
            this.waitForEventTimestamp<IEventData>({
                event: EGameEvents.TENSION_START_$
            }),
            spinEndPromise
        ]);

        const tensionPromise = Promise.race([
            this.waitForEventTimestamp<IEventData>({
                event: EGameEvents.TENSION_START_$
            }),
            spinEndPromise
        ]).then((response) => {
            if (response.data.eventName !== EGameEvents.TENSION_START_$) return undefined;
            return Promise.race([
                this.waitForEventTimestamp<IEventData>({
                    event: EGameEvents.TENSION_WIN_$
                }),
                this.waitForEventTimestamp<IEventData>({
                    event: EGameEvents.TENSION_LOSE_$
                })
            ]).then((tensionEnd) => ({
                start: response.timestamp,
                end: tensionEnd.timestamp
            }));
        });

        const [spinResponse, spinEnd, takeResponse, tension, normalSpinEnd] = await Promise.all([
            spinResponsePromise,
            spinEndPromise,
            takeResponsePromise,
            tensionPromise,
            normalSpinEndPromise
        ]);

        const bet = this.spinBet;
        if (bet == undefined) return;
        const { win: winAmount } = gameInstance.gameStore.get(EGameStoreKeys.SpinOutcome);
        if (winAmount) await EventsCenter.waitForEvent(EGameStates.COMPLETE_WIN_STATE);

        gameInstance.fpsTracker.stopSession();

        const avgFps = gameInstance.fpsTracker.getSessionAverageFPS();
        const minFps = gameInstance.fpsTracker.getSessionMinFPS();
        const maxFps = gameInstance.fpsTracker.getSessionMaxFPS();
        const quickStop = gameInstance.gameStore.get(EGameStoreKeys.IsQuickStopInitiated);
        const responseSpinTime = this.getElapsedTime(spinStart, spinResponse.timestamp);
        const responseTakeTime = takeResponse ? this.getElapsedTime(spinStart, takeResponse.timestamp) : undefined;
        const totalResponseTime = responseSpinTime + (responseTakeTime ?? 0);
        const normalSpinTime = this.getElapsedTime(spinStart, normalSpinEnd.timestamp);
        const tensionTime = tension ? this.getElapsedTime(tension.start, tension.end) : undefined;
        const totalSpinTime = this.getElapsedTime(spinStart, spinEnd.timestamp);

        const statistics: IStatistics = {
            bet,
            responseTimes: {
                spin: responseSpinTime,
                take: responseTakeTime,
                total: totalResponseTime
            },
            spinTimes: {
                normalSpin: normalSpinTime,
                tension: tensionTime,
                total: totalSpinTime
            },
            quickStop,
            winAmount,
            avgFps,
            minFps,
            maxFps
        };

        const payload = this.createSpinSummaryMessage(
            { source: LoggerSource.Game, message: 'spin summary' },
            statistics,
            spinResponse.data.response.actionName
        );

        if (payload) this.log(LoggerLevel.SpinSummary, payload?.log, payload);
    }

    private sendGameStartKibanaPayload() {
        const payload = this.createGameStartMessage({ source: LoggerSource.Game, message: 'game start' });
        if (payload) this.log(LoggerLevel.GameStart, payload?.log, payload);
    }

    private setupGlobalErrorHandlers() {
        window.addEventListener('error', (event) => {
            const payload = this.createErrorMessage(event.error ?? event.message, LoggerSource.Game);
            this.log(LoggerLevel.Error, payload.log, payload);
        });

        window.addEventListener('unhandledrejection', (event) => {
            let error: Error | string;
            if (event.reason instanceof Error) error = event.reason;
            else if (typeof event.reason === 'string') error = event.reason;
            else {
                try {
                    error = JSON.stringify(event.reason);
                } catch {
                    error = 'Unknown rejection reason';
                }
            }

            const payload = this.createErrorMessage(error, LoggerSource.Game);
            this.log(LoggerLevel.Error, payload.log, payload);
        });

        this.overrideConsole('warn', LoggerLevel.Warning, LoggerSource.Game);
        this.overrideConsole('error', LoggerLevel.Error, LoggerSource.Game);
    }

    private overrideConsole(method: 'warn' | 'error', level: LoggerLevel, source: LoggerSource) {
        const original = console[method];
        console[method] = (...args: any[]) => {
            original(...args);
            const message = args.map((arg) => (typeof arg === 'string' ? arg : JSON.stringify(arg))).join(' ');
            const payload = this.createErrorMessage(message, source);
            this.log(level, payload.log, payload);
        };
    }
}

export default new Logger();
